(() => {
  const tabbedComponents = document.querySelectorAll('.tabs');

  if (!tabbedComponents.length) {
    return;
  }

  /**
   * Check if the content of an element overflows its container.
   * @param {HTMLElement} element - The element to check for overflow.
   * @returns {boolean} - True if overflow exists, otherwise false.
   */
  const checkForOverflow = (element) =>
    element.scrollWidth > element.clientWidth;

  /**
   * Handle the resizing of the tabs wrapper and update cursor based on overflow.
   * @param {HTMLElement} tabsWrapper - The tabs wrapper element.
   * @returns {void}
   */
  const handleResize = (tabsWrapper) => {
    tabsWrapper.isOverflowing = checkForOverflow(tabsWrapper);
    tabsWrapper.style.cursor = tabsWrapper.isOverflowing ? 'grab' : '';
  };

  /**
   * Handle the mouse down event on the tabs wrapper to initiate dragging.
   * @param {HTMLElement} tabsWrapper - The tabs wrapper element.
   * @param {MouseEvent} event - The mouse event.
   * @returns {void}
   */
  const handleMouseDown = (tabsWrapper, event) => {
    if (event.target !== tabsWrapper || !tabsWrapper.isOverflowing) {
      return;
    }

    tabsWrapper.isDragging = true;
    tabsWrapper.startPosition = event.clientX - tabsWrapper.offsetLeft;
    tabsWrapper.style.cursor = 'grabbing';
    tabsWrapper.classList.add('is-dragged');
  };

  /**
   * Handle the mouse move event on the tabs wrapper to adjust scroll position.
   * @param {HTMLElement} tabsWrapper - The tabs wrapper element.
   * @param {MouseEvent} event - The mouse event.
   * @returns {void}
   */
  const handleMouseMove = (tabsWrapper, event) => {
    if (!tabsWrapper.isDragging) {
      return;
    }
    const newScrollLeft = event.clientX - tabsWrapper.startPosition;
    tabsWrapper.scrollLeft = tabsWrapper.scrollPosition - newScrollLeft;
  };

  /**
   * Handle the mouse up event on the tabs wrapper to end dragging.
   * @param {HTMLElement} tabsWrapper - The tabs wrapper element.
   * @param {MouseEvent} event - The mouse event.
   * @returns {void}
   */
  const handleMouseUp = (tabsWrapper, event) => {
    if (!tabsWrapper.isDragging) {
      return;
    }

    tabsWrapper.isDragging = false;
    tabsWrapper.classList.remove('is-dragged');
    tabsWrapper.scrollPosition = tabsWrapper.scrollLeft;
    tabsWrapper.startPosition = null;
    tabsWrapper.style.cursor = 'grab';
    event.preventDefault();
  };

  /**
   * Handle the mouse leave event on the tabs wrapper to end dragging.
   * @param {HTMLElement} tabsWrapper - The tabs wrapper element.
   * @returns {void}
   */
  const handleMouseLeave = (tabsWrapper) => {
    if (!tabsWrapper.isDragging) {
      return;
    }

    tabsWrapper.isDragging = false;
    tabsWrapper.classList.remove('is-dragged');
    tabsWrapper.scrollPosition = tabsWrapper.scrollLeft;
    tabsWrapper.startPosition = null;
  };

  tabbedComponents.forEach((component) => {
    const tabsWrapper = component.querySelector('.tab-list');
    const panelsWrapper = component.querySelector('.panel-list');

    if (!tabsWrapper || !panelsWrapper) {
      return;
    }

    const tabs = tabsWrapper.querySelectorAll('[role="tab"]');
    const panels = panelsWrapper.querySelectorAll('[role="tabpanel"]');

    if (!tabs.length || !panels.length) {
      return;
    }

    tabsWrapper.isOverflowing = checkForOverflow(tabsWrapper);
    tabsWrapper.isDragging = false;
    tabsWrapper.startPosition = null;
    tabsWrapper.scrollPosition = 0;

    const debounce = (func, delay) => {
      let timeoutId;

      return function (...args) {
        clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    };

    window.addEventListener(
      'resize',
      debounce(() => handleResize(tabsWrapper), 100),
    );

    // Initial cursor type
    tabsWrapper.style.cursor = tabsWrapper.isOverflowing ? 'grab' : '';

    tabsWrapper.addEventListener('mousedown', (event) =>
      handleMouseDown(tabsWrapper, event),
    );
    tabsWrapper.addEventListener('mousemove', (event) =>
      handleMouseMove(tabsWrapper, event),
    );
    tabsWrapper.addEventListener('mouseup', (event) =>
      handleMouseUp(tabsWrapper, event),
    );
    tabsWrapper.addEventListener('mouseleave', () =>
      handleMouseLeave(tabsWrapper),
    );

    let activeTabIndex = 0;

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        if (index === activeTabIndex) {
          return;
        }

        tabs[activeTabIndex].setAttribute('aria-selected', false);
        panels[activeTabIndex].classList.add('panel-hidden');

        // Show the clicked tab and corresponding panel
        tab.setAttribute('aria-selected', true);
        panels[index].classList.remove('panel-hidden');

        activeTabIndex = index;
      });
    });
  });
})();
